/**
 * The story graph rules (spec 010), in one place for the editor store and the
 * story operations (spec 023): node placement, removing nodes, and the
 * connect and condition rules. Generic over the node shape, so the store's
 * `StoryGraph` (nodes by `storySceneId`) and the `StoryDocument` (nodes by
 * scene key) share them.
 */
import type { ChoiceOption, StoryGraphEdge } from '../@types';

/** Horizontal step between a new node and the right-most node. */
export const NODE_SPACING_X = 280;
/** Vertical step between stacked nodes (placement after a node, `arrangeGraph`). */
export const NODE_ROW_HEIGHT = 160;
/** Where the first node of an empty graph goes. */
export const FIRST_NODE_POSITION = { x: 100, y: 100 } as const;

export interface PositionedNode {
  id: string;
  position: { x: number; y: number };
}

/** Free space to the right of the right-most node. */
export function nextNodePosition(nodes: readonly PositionedNode[]): { x: number; y: number } {
  if (nodes.length === 0) return { ...FIRST_NODE_POSITION };
  const rightMost = nodes.reduce((a, b) => (b.position.x > a.position.x ? b : a));
  return { x: rightMost.position.x + NODE_SPACING_X, y: rightMost.position.y };
}

/**
 * One column to the right of `after`, level with it, or below the nodes
 * already there (siblings of the same parent stack top to bottom).
 */
export function positionAfter(nodes: readonly PositionedNode[], after: PositionedNode): { x: number; y: number } {
  const x = after.position.x + NODE_SPACING_X;
  let y = after.position.y;
  const taken = (yy: number) => nodes.some((n) =>
    Math.abs(n.position.x - x) < NODE_SPACING_X / 2 && Math.abs(n.position.y - yy) < NODE_ROW_HEIGHT / 2);
  while (taken(y)) y += NODE_ROW_HEIGHT;
  return { x, y };
}

/**
 * Remove nodes and every edge touching them; keep the start on a node (the
 * first remaining one when the start went). Assigns new arrays, so it works
 * on an Immer draft and, on a shallow copy, leaves the input untouched.
 */
export function dropNodes(
  graph: { nodes: PositionedNode[]; edges: StoryGraphEdge[]; startNodeId: string },
  nodeIds: ReadonlySet<string>,
): void {
  graph.nodes = graph.nodes.filter((n) => !nodeIds.has(n.id));
  graph.edges = graph.edges.filter((e) => !nodeIds.has(e.from) && !nodeIds.has(e.to));
  if (!graph.nodes.some((n) => n.id === graph.startNodeId)) {
    graph.startNodeId = graph.nodes[0]?.id ?? '';
  }
}

/** The choices that have no edge from `nodeId` yet. */
export function availableChoicesFrom(
  edges: readonly StoryGraphEdge[],
  nodeId: string,
  choices: readonly ChoiceOption[],
): ChoiceOption[] {
  const used = new Set(edges.filter((e) => e.from === nodeId && e.condition).map((e) => e.condition));
  return choices.filter((c) => !used.has(c.id));
}

export type ConnectRefusal = 'missing-node' | 'needs-choice' | 'choice-taken' | 'not-a-choice' | 'next-exists' | 'no-choices';

/** The author-facing reason for each refusal (the editor's inline notice). */
export const CONNECT_MESSAGES: Readonly<Record<ConnectRefusal, string>> = {
  'missing-node': 'Connect two scenes on the graph',
  'needs-choice': 'Pick the choice this arrow follows',
  'not-a-choice': 'That choice is not on this scene',
  'choice-taken': 'That choice already has an arrow',
  'no-choices': 'This scene has no choices',
  'next-exists': 'This scene already has a next scene. Delete that arrow first.',
};

/** The edge fields a permitted connection gets (beside its id, from and to). */
export type EdgeRouting = { condition?: string; label?: string };

export type RuleResult<R extends string> =
  | ({ ok: true } & EdgeRouting)
  | { ok: false; reason: R; message: string };

const refuse = <R extends ConnectRefusal>(reason: R) => ({ ok: false as const, reason, message: CONNECT_MESSAGES[reason] });

/**
 * May `from` → `to` be connected (spec 010)? A scene with choices needs a
 * free choice; one without takes at most one unconditional edge.
 * `choicesOf` gives the choices of a node's scene.
 */
export function connectRule<N extends PositionedNode>(
  graph: { nodes: readonly N[]; edges: readonly StoryGraphEdge[] },
  choicesOf: (node: N) => readonly ChoiceOption[],
  from: string,
  to: string,
  condition?: string,
): RuleResult<ConnectRefusal> {
  const source = graph.nodes.find((n) => n.id === from);
  const target = graph.nodes.find((n) => n.id === to);
  if (!source || !target) return refuse('missing-node');
  const choices = choicesOf(source);
  if (choices.length > 0) {
    if (!condition) return refuse('needs-choice');
    const choice = choices.find((c) => c.id === condition);
    if (!choice) return refuse('not-a-choice');
    if (graph.edges.some((e) => e.from === from && e.condition === condition)) return refuse('choice-taken');
    return { ok: true, condition, label: choice.text ?? '' };
  }
  if (condition) return refuse('no-choices');
  if (graph.edges.some((e) => e.from === from && !e.condition)) return refuse('next-exists');
  return { ok: true };
}

export type ConditionRefusal = 'missing-edge' | 'not-a-choice' | 'choice-taken' | 'needs-choice' | 'next-exists';

const CONDITION_MESSAGES: Readonly<Record<ConditionRefusal, string>> = {
  'missing-edge': 'That arrow is not on the graph',
  'not-a-choice': 'That choice is not on this scene',
  'choice-taken': 'That choice already has an arrow',
  'needs-choice': 'This scene cannot have an unconditional arrow',
  'next-exists': 'This scene cannot have an unconditional arrow',
};

/**
 * May the edge's condition become `condition` (a choice id) or be cleared
 * (null), under the per-node rules (spec 010)? On success the result carries
 * the edge's new condition and label (both absent when cleared).
 */
export function edgeConditionRule<N extends PositionedNode>(
  graph: { nodes: readonly N[]; edges: readonly StoryGraphEdge[] },
  choicesOf: (node: N) => readonly ChoiceOption[],
  edgeId: string,
  condition: string | null,
): RuleResult<ConditionRefusal> {
  const fail = (reason: ConditionRefusal) => ({ ok: false as const, reason, message: CONDITION_MESSAGES[reason] });
  const edge = graph.edges.find((e) => e.id === edgeId);
  if (!edge) return fail('missing-edge');
  const source = graph.nodes.find((n) => n.id === edge.from);
  const choices = source ? choicesOf(source) : [];
  const siblings = graph.edges.filter((e) => e.from === edge.from && e.id !== edgeId);
  if (condition) {
    const choice = choices.find((c) => c.id === condition);
    if (!choice) return fail('not-a-choice');
    if (siblings.some((e) => e.condition === condition)) return fail('choice-taken');
    return { ok: true, condition: choice.id, label: choice.text };
  }
  if (choices.length > 0) return fail('needs-choice');
  if (siblings.some((e) => !e.condition)) return fail('next-exists');
  return { ok: true };
}
